import { Menu } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Button } from '@/components/atoms/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/atoms/dialog';
import { UserMenu } from '@/features/auth/components/UserMenu';
import { useSession } from '@/features/auth/context/SessionContext';
import { cn } from '@/libs/utils/cn';
import { Logo } from '../atoms/logo';

const MAP_ITEM = { label: 'Map', href: '/map' };
const EXPORT_ITEM = { label: 'Export', href: '/export' };
// The Zones screen is open to every authenticated user (read-only); per-row
// writes are admin-gated server-side.
const ZONES_ITEM = { label: 'Zones', href: '/zones' };
const NAV_ITEMS = [MAP_ITEM, EXPORT_ITEM, ZONES_ITEM];

function isNavActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Always reserved. The map fills it with the workspace switcher; Export and Zones leave it empty
 * so Map, Export and Zones stay in place. */
export const NAVBAR_WORKSPACE_SLOT_ID = 'navbar-workspace';

export function Navbar() {
  const location = useLocation();
  const { user, status } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="fixed top-0 z-50 flex h-14 w-full items-center border-b border-muted bg-background px-4 md:px-12">
      <Logo className="shrink-0 py-2.5" />
      <div id={NAVBAR_WORKSPACE_SLOT_ID} className="flex min-w-0 flex-1 items-center" />
      <div className="flex items-center gap-2 md:gap-4">
        <nav className="hidden md:block" aria-label="Main navigation">
          {/* A segmented control: the active section sits on a raised white segment. */}
          <ul className="mx-4 flex items-center gap-0.5 rounded-full bg-muted p-1">
            {NAV_ITEMS.map((item) => {
              const isActive = isNavActive(location.pathname, item.href);
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'block rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-background text-primary-700 shadow-sm'
                        : 'text-muted-foreground hover:text-primary-700',
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label="Menu"
          onClick={() => setMenuOpen(true)}
        >
          <Menu />
        </Button>
        <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader className="gap-1">
              <DialogTitle className="text-primary-700 text-lg font-normal">Menu</DialogTitle>
            </DialogHeader>
            <nav className="flex flex-col gap-1" aria-label="Pages">
              {NAV_ITEMS.map((item) => {
                const isActive = isNavActive(location.pathname, item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      'rounded-md px-3 py-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-accent text-primary'
                        : 'text-foreground hover:bg-accent hover:text-primary',
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </DialogContent>
        </Dialog>
        {status === 'signed-in' && user ? <UserMenu user={user} /> : null}
      </div>
    </div>
  );
}
